// Cannot import CSS here as we load tagging.js in the outer frame,
// but the CSS would need to apply to the document frame.
import { Item, ItemSelection, ItemType } from '@cfl/ixbrl-tag-renderer';
import { HighlightRenderer } from '@cfl/ixbrl-tag-renderer/lib/fixed-layout/renderer';
import { invalidateAll } from 'src/shared/singleton-state';

import { documentFrameDocument, documentFrameWindow } from '../common/dom-utils';

/*
 * Tagging functionality support for EPUB and XHTML uploads
 */
declare function setTagDetails(element: JQuery, val: string): void;

const ITEM_TYPE_REGEX = /^(tag|footnote|comment)$/;
class SeahorseItem implements Item {
  element: HTMLElement;
  highlightContainer: HTMLElement;
  highlightElements: HTMLElement[] = [];
  type: ItemType;

  constructor(element: HTMLElement, type: ItemType) {
    this.type = type;
    this.element = element;
    this.highlightContainer = element;
  }
  classes: string[] = [];
  handleClasses: string[] = [];

  getDropdownItem(): HTMLElement {
    const dropdownItem = documentFrameDocument().createElementNS('http://www.w3.org/1999/xhtml', 'div');
    dropdownItem.classList.add('nestedTagDropdownItem');
    // todo this must not be keyed to the something injected by sh
    dropdownItem.textContent = this.element.getAttribute('sh:dropdownname');

    const updateSelected = () => {
      if (this.element.classList.contains('selectedCommonTag') || this.element.classList.contains('selectedManualTag')) {
        dropdownItem.classList.add('selected');
      } else {
        dropdownItem.classList.remove('selected');
      }
    };

    const mutationObserver = new MutationObserver((m) => m.some((i) => i.attributeName === 'class') && updateSelected());
    mutationObserver.observe(this.element, { attributes: true });
    updateSelected();

    dropdownItem.onclick = () => {
      const tagId = this.element.getAttribute('sh:tagid')!.valueOf();
      setTagDetails($(this.element), tagId);
    };

    return dropdownItem;
  }

  public getItemType(): ItemType {
    return this.type;
  }

  getItemId(): string {
    const id =
      this.element.getAttribute('sh:tagid') || this.element.getAttribute('sh:commentid') || this.element.getAttribute('sh:footnoteid');
    return id ? id.valueOf() : '';
  }
}

function itemFactory(element: HTMLElement): Item | undefined {
  if (!element?.classList) {
    return undefined;
  }
  for (let i = 0; i < element.classList.length; ++i) {
    const match = ITEM_TYPE_REGEX.exec(element.classList[i]);
    if (match && match[0]) {
      return new SeahorseItem(element, match[0].valueOf() as ItemType);
    }
  }
  return undefined;
}

function getSelectionSegmentElements(): Map<string, ItemSelection> {
  const tags = [...documentFrameDocument().getElementsByClassName('tag')];
  const footnotes = [...documentFrameDocument().getElementsByClassName('footnote')];
  const comments = [...documentFrameDocument().getElementsByClassName('comment')];
  const segments = [...documentFrameDocument().getElementsByClassName('selectionSegment')];
  const itemSelectionsById: Map<string, ItemSelection> = new Map();
  const document = documentFrameDocument();
  const dfWindow = documentFrameWindow();
  tags
    .concat(footnotes)
    .concat(comments)
    .concat(segments)
    .forEach((element) => {
      const selectionIdArray = element
        .getAttribute('class')!
        .split(' ')
        .filter((x) => x.startsWith('selection_'));
      for (const selectionId of selectionIdArray) {
        const itemSelection = itemSelectionsById.get(selectionId);
        if (itemSelection === undefined) {
          const item = itemFactory(element as HTMLElement)!;
          itemSelectionsById.set(
            selectionId,
            new ItemSelection(document, dfWindow, item, 'XHTML', itemFactory, {
              nestedTagIconUrl: window.rootDirectory + 'img/NestedTagIcon.png',
            }),
          );
        } else {
          itemSelection.add(element as HTMLElement);
        }
      }
    });

  return itemSelectionsById;
}

export function isNewStyleTagging() {
  return documentFrameDocument().body.classList.contains('newStyleTagging');
}

export function initNewStyleTagging() {
  window.__destroyHighlights?.();
  setTitleTooltipToEmpty();
  setBodyId();
  const itemSelectionsById = getSelectionSegmentElements();
  const renderer = new HighlightRenderer(documentFrameDocument(), Array.from(itemSelectionsById.values()));
  renderer.nestedTagsDropdown.filter = (elements) => !elements.some((e) => e.classList.contains('syntheticTable'));
  renderer.render();
  const body = documentFrameDocument().body;
  let dropdownActivated = true;
  const observer = new MutationObserver((mutations) =>
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        renderer.nestedTagsDropdown.activate(
          dropdownActivated && !body.classList.contains('creatingTable') && !body.classList.contains('usingSuggestedTable'),
        );
      }
    }),
  );
  window.__suspendNestedTagsDropdown = () => {
    dropdownActivated = false;
    renderer.nestedTagsDropdown.activate(false);
    return () => {
      dropdownActivated = true;
      renderer.nestedTagsDropdown.activate(true);
    };
  };
  observer.observe(body, { attributes: true });
  window.__destroyHighlights = () => {
    observer.disconnect();
    renderer.dispose();
  };
}

export function setPageVersion(pageVersion: number | undefined): void {
  if (pageVersion != undefined && pageVersion > 0) {
    window.__pageVersion = pageVersion;
  }
}

export function setExtensionTaxonomyGuid(extensionTaxonomyGuid: string | undefined): void {
  const currentExtensionTaxonomyGuid = window.__extensionTaxonomyGuid;
  if (extensionTaxonomyGuid && currentExtensionTaxonomyGuid !== extensionTaxonomyGuid) {
    window.__extensionTaxonomyGuid = extensionTaxonomyGuid;
    if (currentExtensionTaxonomyGuid) {
      invalidateAll('extensionTaxonomyGuid', extensionTaxonomyGuid);
    }
  }
}

function setTitleTooltipToEmpty(): void {
  const stackerPage = documentFrameDocument().getElementsByTagName('body')[0].getElementsByClassName('stacker-page');
  if (stackerPage.length > 0) {
    stackerPage[0].setAttribute('title', '');
  }
}

function setBodyId() {
  const bodyDiv = documentFrameDocument().getElementsByTagName('body')[0];
  bodyDiv.id = 'body';
}
